import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, CalendarX2, Save, Copy, RefreshCw, Link2, DownloadCloud, CalendarSync } from "lucide-react";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { Label, Input, PrimaryButton, GhostButton } from "@/components/fields";

export default function VillaAdminPanel() {
  const [settings, setSettings] = useState({
    nightly_rate: 450, min_nights: 3, deposit_percent: 30, ics_token: "",
  });
  const [feeds, setFeeds] = useState([]);
  const [newFeed, setNewFeed] = useState({ label: "", url: "" });
  const [syncing, setSyncing] = useState(false);
  const [blocks, setBlocks] = useState([]);
  const [reserved, setReserved] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [newBlock, setNewBlock] = useState({ start: "", end: "", label: "" });
  const [newPeriod, setNewPeriod] = useState({ name: "", start: "", end: "", nightly_rate: "", deposit_percent: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [s, b, p] = await Promise.all([
      api.get("/admin/villa/settings"),
      api.get("/admin/villa/blocks"),
      api.get("/admin/villa/periods"),
    ]);
    setSettings(s.data);
    setFeeds(s.data.ical_feeds || []);
    setBlocks(b.data.blocks);
    setReserved(b.data.reserved);
    setPeriods(p.data);
  };

  useEffect(() => {
    load().catch((e) => toast.error(formatApiError(e)));
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put("/admin/villa/settings", {
        nightly_rate: Number(settings.nightly_rate),
        min_nights: Number(settings.min_nights),
        deposit_percent: Number(settings.deposit_percent),
      });
      toast.success("Villa settings saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const addFeed = async () => {
    if (!newFeed.label.trim() || !newFeed.url.trim()) return toast.error("Give the feed a channel name and URL");
    try {
      const { data } = await api.post("/admin/villa/ical-feeds", newFeed);
      if (data.error) toast.error(`${data.label} connected, but the first sync failed: ${data.error}`);
      else toast.success(`${data.label} connected — ${data.imported ?? 0} booking${data.imported === 1 ? "" : "s"} imported`);
      setNewFeed({ label: "", url: "" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeFeed = async (feed) => {
    if (!window.confirm(`Remove the ${feed.label} feed? Its imported blocks will be deleted.`)) return;
    try {
      await api.delete(`/admin/villa/ical-feeds/${feed.id}`);
      toast.success(`${feed.label} feed removed`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/villa/ical-sync");
      const failed = (data.feeds || []).filter((f) => f.error);
      if (data.skipped) toast.error("No feeds connected");
      else if (failed.length) toast.error(`Synced with errors — ${failed.map((f) => f.label).join(", ")} failed`);
      else toast.success(`Imported ${data.imported} external booking${data.imported === 1 ? "" : "s"} across ${(data.feeds || []).length} feed${(data.feeds || []).length === 1 ? "" : "s"}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSyncing(false);
    }
  };

  const addBlock = async () => {
    if (!newBlock.start || !newBlock.end) return toast.error("Pick a start and end date");
    if (newBlock.end <= newBlock.start) return toast.error("End must be after start");
    try {
      await api.post("/admin/villa/blocks", { ...newBlock, label: newBlock.label || "Blocked" });
      toast.success("Dates blocked");
      setNewBlock({ start: "", end: "", label: "" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteBlock = async (id) => {
    try {
      await api.delete(`/admin/villa/blocks/${id}`);
      toast.success("Block removed");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addPeriod = async () => {
    if (!newPeriod.name || !newPeriod.start || !newPeriod.end || !newPeriod.nightly_rate) {
      return toast.error("Fill in name, dates and rate");
    }
    if (newPeriod.end <= newPeriod.start) return toast.error("End must be after start");
    try {
      const payload = { name: newPeriod.name, start: newPeriod.start, end: newPeriod.end, nightly_rate: Number(newPeriod.nightly_rate) };
      if (newPeriod.deposit_percent !== "") payload.deposit_percent = Number(newPeriod.deposit_percent);
      await api.post("/admin/villa/periods", payload);
      toast.success("Season added");
      setNewPeriod({ name: "", start: "", end: "", nightly_rate: "", deposit_percent: "" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deletePeriod = async (id) => {
    try {
      await api.delete(`/admin/villa/periods/${id}`);
      toast.success("Season removed");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const icsUrl = `${process.env.REACT_APP_BACKEND_URL}/api/calendar/villa.ics?token=${settings.ics_token}`;

  const copyIcs = async () => {
    try {
      await navigator.clipboard.writeText(icsUrl);
      toast.success("Calendar link copied");
    } catch {
      toast.error("Copy failed — select the link manually");
    }
  };

  const regenerateIcs = async () => {
    if (!window.confirm("Regenerate the calendar link? Any calendar using the old link stops updating.")) return;
    try {
      const { data } = await api.post("/admin/villa/ics-token");
      setSettings((s) => ({ ...s, ics_token: data.ics_token }));
      toast.success("New calendar link generated");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const icalBlocks = blocks.filter((b) => b.source === "ical");
  const manualBlocks = blocks.filter((b) => b.source !== "ical");

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="admin-villa">
      <div className="space-y-6">
        <div className="bg-white rounded-3xl border border-terracotta/10 p-6 sm:p-8">
          <h3 className="font-serif text-2xl text-ink mb-6">Rates & deposit</h3>
          <div className="space-y-4">
            <div>
              <Label>Base nightly rate (USD)</Label>
              <Input type="number" min="1" data-testid="villa-rate-input" value={settings.nightly_rate} onChange={(e) => setSettings({ ...settings, nightly_rate: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Minimum nights</Label>
                <Input type="number" min="1" max="30" data-testid="villa-min-nights-input" value={settings.min_nights} onChange={(e) => setSettings({ ...settings, min_nights: e.target.value })} />
              </div>
              <div>
                <Label>Base deposit %</Label>
                <Input type="number" min="0" max="100" data-testid="villa-deposit-input" value={settings.deposit_percent} onChange={(e) => setSettings({ ...settings, deposit_percent: e.target.value })} />
              </div>
            </div>
            <PrimaryButton data-testid="villa-settings-save-btn" onClick={saveSettings} disabled={saving}>
              <Save size={15} /> {saving ? "Saving…" : "Save settings"}
            </PrimaryButton>
            <p className="text-xs text-ink/45 leading-relaxed">
              The base rate powers the public calendar and enquiry estimates. Seasons below override rate (and optionally deposit %) on their dates.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-3xl border border-terracotta/10 p-6 sm:p-8" data-testid="ical-import-card">
          <h3 className="font-serif text-2xl text-ink mb-2">Import external calendars</h3>
          <p className="text-xs text-ink/45 leading-relaxed mb-4">
            Connect Airbnb, VRBO, Booking.com or any iCal export link. Bookings from every channel automatically block those dates here — synced every 6 hours and whenever you press "Sync all".
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label>Channel name</Label>
              <Input data-testid="ical-feed-label-input" value={newFeed.label} onChange={(e) => setNewFeed({ ...newFeed, label: e.target.value })} placeholder="Airbnb, VRBO…" />
            </div>
            <div className="sm:col-span-2">
              <Label>iCal feed URL</Label>
              <Input data-testid="ical-feed-url-input" value={newFeed.url} onChange={(e) => setNewFeed({ ...newFeed, url: e.target.value })} placeholder="https://www.airbnb.com/calendar/ical/…" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <PrimaryButton data-testid="ical-feed-add-btn" onClick={addFeed} className="!px-4 !py-2 text-xs">
              <Plus size={13} /> Connect feed
            </PrimaryButton>
            <GhostButton data-testid="ical-sync-btn" onClick={syncNow} disabled={syncing || feeds.length === 0} className="!px-4 !py-2 text-xs">
              <DownloadCloud size={13} /> {syncing ? "Syncing…" : "Sync all now"}
            </GhostButton>
          </div>
          <div className="mt-4 space-y-2">
            {feeds.length === 0 && <p className="text-sm text-ink/40" data-testid="ical-feeds-empty">No external calendars connected yet.</p>}
            {feeds.map((f) => (
              <div key={f.id} className="flex items-center gap-3 rounded-2xl bg-sky-50 border border-sky-200 px-4 py-3 text-sm" data-testid={`ical-feed-${f.id}`}>
                <CalendarSync size={15} className="text-sky-600 shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium truncate">{f.label}</p>
                  <p className="text-[11px] text-ink/45 truncate" data-testid={`ical-feed-status-${f.id}`}>
                    {f.last_error ? (
                      <span className="text-destructive">Sync error: {f.last_error}</span>
                    ) : f.last_sync ? (
                      <>Last sync {fmtDate(f.last_sync)} — {f.event_count ?? 0} booking{(f.event_count ?? 0) === 1 ? "" : "s"} blocked</>
                    ) : (
                      "Not synced yet"
                    )}
                  </p>
                </div>
                <button data-testid={`ical-feed-delete-${f.id}`} onClick={() => removeFeed(f)} className="ml-auto p-1.5 rounded-full hover:bg-destructive/10 text-destructive transition-colors" aria-label={`Remove ${f.label} feed`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-3xl border border-terracotta/10 p-6 sm:p-8">
          <h3 className="font-serif text-2xl text-ink mb-4">Share to calendars (iCal)</h3>
          <p className="text-xs text-ink/45 leading-relaxed mb-3">
            Paste this link into Airbnb, Google Calendar or any calendar app as an iCal subscription — confirmed stays and blocked dates sync out automatically.
          </p>
          <div className="flex items-center gap-2 rounded-xl border border-ink/10 bg-sand px-3 py-2.5" data-testid="ics-feed-url">
            <Link2 size={14} className="text-teal shrink-0" />
            <span className="text-xs font-mono text-ink/60 truncate grow">{icsUrl}</span>
          </div>
          <div className="flex gap-2 mt-3">
            <GhostButton data-testid="ics-copy-btn" onClick={copyIcs} className="!px-4 !py-2 text-xs">
              <Copy size={13} /> Copy link
            </GhostButton>
            <GhostButton data-testid="ics-regenerate-btn" onClick={regenerateIcs} className="!px-4 !py-2 text-xs">
              <RefreshCw size={13} /> Regenerate
            </GhostButton>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <div className="bg-white rounded-3xl border border-terracotta/10 p-6 sm:p-8" data-testid="admin-periods">
          <h3 className="font-serif text-2xl text-ink mb-6">Seasonal rates</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Season name</Label>
              <Input data-testid="period-name-input" value={newPeriod.name} onChange={(e) => setNewPeriod({ ...newPeriod, name: e.target.value })} placeholder="e.g. High season" />
            </div>
            <div>
              <Label>Nightly rate (USD)</Label>
              <Input type="number" min="1" data-testid="period-rate-input" value={newPeriod.nightly_rate} onChange={(e) => setNewPeriod({ ...newPeriod, nightly_rate: e.target.value })} placeholder="650" />
            </div>
            <div>
              <Label>From</Label>
              <Input type="date" data-testid="period-start-input" value={newPeriod.start} onChange={(e) => setNewPeriod({ ...newPeriod, start: e.target.value })} />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" data-testid="period-end-input" value={newPeriod.end} onChange={(e) => setNewPeriod({ ...newPeriod, end: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Deposit % for this season (optional)</Label>
              <Input type="number" min="0" max="100" data-testid="period-deposit-input" value={newPeriod.deposit_percent} onChange={(e) => setNewPeriod({ ...newPeriod, deposit_percent: e.target.value })} placeholder={`Leave blank to use base ${settings.deposit_percent}%`} />
            </div>
          </div>
          <PrimaryButton data-testid="period-add-btn" onClick={addPeriod} className="mt-4">
            <Plus size={15} /> Add season
          </PrimaryButton>
          <div className="mt-6 space-y-2">
            {periods.length === 0 && <p className="text-sm text-ink/40">No seasons — the base rate applies year-round.</p>}
            {periods.map((p) => (
              <div key={p.id} className="flex items-center gap-3 rounded-2xl bg-gold/10 border border-gold/25 px-4 py-3 text-sm" data-testid={`period-${p.id}`}>
                <span className="font-medium">{p.name}</span>
                <span className="text-ink/50 text-xs">{fmtDate(p.start)} → {fmtDate(p.end)}</span>
                <span className="font-semibold text-terracotta ml-auto">
                  ${p.nightly_rate}/nt{p.deposit_percent != null ? ` · ${p.deposit_percent}% dep` : ""}
                </span>
                <button data-testid={`period-delete-${p.id}`} onClick={() => deletePeriod(p.id)} className="p-1.5 rounded-full hover:bg-destructive/10 text-destructive transition-colors" aria-label="Remove season">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-3xl border border-terracotta/10 p-6 sm:p-8">
          <h3 className="font-serif text-2xl text-ink mb-6">Blocked dates</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>From</Label>
              <Input type="date" data-testid="block-start-input" value={newBlock.start} onChange={(e) => setNewBlock({ ...newBlock, start: e.target.value })} />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" data-testid="block-end-input" value={newBlock.end} onChange={(e) => setNewBlock({ ...newBlock, end: e.target.value })} />
            </div>
          </div>
          <div className="mt-3">
            <Label>Reason (optional)</Label>
            <Input data-testid="block-label-input" value={newBlock.label} onChange={(e) => setNewBlock({ ...newBlock, label: e.target.value })} placeholder="e.g. Family stay, maintenance" />
          </div>
          <PrimaryButton data-testid="block-add-btn" onClick={addBlock} className="mt-4">
            <Plus size={15} /> Block these dates
          </PrimaryButton>

          <div className="mt-8 space-y-2">
            <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-ink/45">Confirmed stays</p>
            {reserved.length === 0 && <p className="text-sm text-ink/40">No confirmed stays yet.</p>}
            {reserved.map((r) => (
              <div key={r.id} className="flex items-center gap-3 rounded-2xl bg-teal/5 border border-teal/15 px-4 py-3 text-sm" data-testid={`reserved-${r.id}`}>
                <CalendarX2 size={15} className="text-teal shrink-0" />
                <span className="font-medium">{fmtDate(r.start)} → {fmtDate(r.end)}</span>
                <span className="text-ink/50 text-xs ml-auto">{r.label}</span>
              </div>
            ))}
            {icalBlocks.length > 0 && (
              <>
                <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-ink/45 pt-4">Imported from external calendars</p>
                {icalBlocks.map((b) => (
                  <div key={b.id} className="flex items-center gap-3 rounded-2xl bg-sky-50 border border-sky-200 px-4 py-3 text-sm" data-testid={`ical-block-${b.id}`}>
                    <CalendarSync size={15} className="text-sky-600 shrink-0" />
                    <span className="font-medium">{fmtDate(b.start)} → {fmtDate(b.end)}</span>
                    <span className="text-ink/50 text-xs">{b.label}</span>
                    <span className="text-[10px] text-sky-600 ml-auto font-mono uppercase tracking-wider">auto-sync</span>
                  </div>
                ))}
              </>
            )}
            <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-ink/45 pt-4">Manual blocks</p>
            {manualBlocks.length === 0 && <p className="text-sm text-ink/40">No manual blocks.</p>}
            {manualBlocks.map((b) => (
              <div key={b.id} className="flex items-center gap-3 rounded-2xl bg-terracotta/5 border border-terracotta/15 px-4 py-3 text-sm" data-testid={`block-${b.id}`}>
                <CalendarX2 size={15} className="text-terracotta shrink-0" />
                <span className="font-medium">{fmtDate(b.start)} → {fmtDate(b.end)}</span>
                <span className="text-ink/50 text-xs">{b.label}</span>
                <button data-testid={`block-delete-${b.id}`} onClick={() => deleteBlock(b.id)} className="ml-auto p-1.5 rounded-full hover:bg-destructive/10 text-destructive transition-colors" aria-label="Remove block">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
